import { forwardRef } from 'react';
import { cn } from '../tokens/cn';
import type { VariantProps } from '../tokens/variants';
import {
  progressBarVariants,
  progressTrackVariants,
} from './progress.variants';

export interface ProgressProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, 'role'>,
    VariantProps<typeof progressTrackVariants> {
  /** 0–100. Clamped. Ignored when `indeterminate`. */
  value?: number;
  /** Indeterminate animation when total/value isn't known yet. */
  indeterminate?: boolean;
  /** Tone of the filled bar. */
  tone?: VariantProps<typeof progressBarVariants>['tone'];
  /** Optional accessible label — exposes value via aria-valuetext. */
  label?: string;
}

/** Linear progress bar. Use `indeterminate` for unknown duration; otherwise
 *  pass `value` (0–100). The bar transitions smoothly between values. */
export const Progress = forwardRef<HTMLDivElement, ProgressProps>(
  (
    {
      value = 0,
      indeterminate = false,
      tone = 'brand',
      size,
      label,
      className,
      ...rest
    },
    ref,
  ) => {
    const clamped = Math.max(0, Math.min(100, Math.round(value)));
    const width = indeterminate ? '40%' : `${clamped}%`;
    return (
      <div
        ref={ref}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        {...(!indeterminate && { 'aria-valuenow': clamped })}
        {...(label && { 'aria-label': label })}
        className={cn(progressTrackVariants({ size }), className)}
        {...rest}
      >
        <div
          className={cn(progressBarVariants({ tone, indeterminate }))}
          style={{ width }}
        />
      </div>
    );
  },
);

Progress.displayName = 'Progress';
